import { useGetInitialsFromDisplayName } from '@pba/react-utils';
import { Avatar as AvatarA, type AvatarProps } from 'antd';
import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { Badge, type BadgeProperties } from '../badge';
import { useAvatarGroupSize } from './avatar-group/AvatarGroupSizeContext';
import styles from './avatar.module.css';
import { type AvatarSize, useAvatarBadgeOffset, useResolveAvatarSize } from './hooks';

export type { AvatarSize } from './hooks';

export type AvatarBorderColor = 'none' | 'white';

export type AvatarProperties = {
  badge?: Omit<BadgeProperties, 'children'>;
  borderColor?: AvatarBorderColor;
  color?: 'default' | 'primary';
  fallbackPhoto: false | string;
  size?: AvatarSize | number;
  src?: { url: string } | string;
} & Omit<AvatarProps, 'size' | 'src'>;

const Avatar = ({
  badge,
  borderColor,
  children,
  className,
  color = 'default',
  fallbackPhoto,
  icon,
  size,
  src,
  ...rest
}: AvatarProperties) => {
  const groupSize = useAvatarGroupSize();
  const getInitialsFromDisplayName = useGetInitialsFromDisplayName();
  const resolveAvatarSize = useResolveAvatarSize();
  const effectiveSize = size ?? groupSize ?? 'md';
  const avatarSize = resolveAvatarSize({ value: effectiveSize });
  const badgeOffset = useAvatarBadgeOffset(avatarSize);
  const content = typeof children === 'string' ? getInitialsFromDisplayName(children) : children;
  const imageFallback = fallbackPhoto === false ? undefined : fallbackPhoto;
  const photoUrl = typeof src === 'string' ? src : src?.url;
  const resolvedSource = photoUrl ?? imageFallback;
  const [avatarSource, setAvatarSource] = useState(resolvedSource);

  const handleError = useCallback(() => {
    if (imageFallback && avatarSource !== imageFallback) {
      setAvatarSource(imageFallback);
      return false;
    }
    setAvatarSource(undefined);
    return true;
  }, [imageFallback, avatarSource]);

  useEffect(() => {
    setAvatarSource(resolvedSource);
  }, [resolvedSource]);

  const implicitBorderColor: AvatarBorderColor = groupSize === undefined ? 'none' : 'white';
  const effectiveBorderColor = borderColor ?? implicitBorderColor;

  const avatarProperties: AvatarProps = {
    ...rest,
    className: cn(
      styles.avatar,
      {
        [styles.default ?? '']: color === 'default',
        [styles.primary ?? '']: color === 'primary',
        [styles['border-color-none'] ?? '']: effectiveBorderColor === 'none',
        [styles['border-color-white'] ?? '']: effectiveBorderColor === 'white',
      },
      className,
    ),
    icon,
    onError: handleError,
    size: avatarSize,
    src: avatarSource,
  };

  if (badge) {
    return (
      <Badge offset={badgeOffset} {...badge}>
        <AvatarA {...avatarProperties}>{content}</AvatarA>
      </Badge>
    );
  }

  return <AvatarA {...avatarProperties}>{content}</AvatarA>;
};

export default Avatar;
